// js/_shared/_role-modal.js
/* =============================================================================
   ROLE SELECTION MODAL
   Path: /js/_shared/_role-modal.js
   
   Purpose: Handle role selection modal for users with both Sponsor and Advocate
   roles for the same event. Now POSTs to /Account/SelectRole to properly
   create advocate records or add sponsor roles before redirecting.
   
   Dependencies: 
   - /js/_shared/_utils.js (load first)
   
   Namespace: window.AppUtils.RoleModal
   Global Alias: window.RoleModal
   
   UPDATED: Now calls SelectRole endpoint instead of simple redirect.
   This ensures:
   - Advocate record is only created when user chooses Advocate path
   - Sponsor role is properly added when user chooses Event path
   - Proper role refresh happens before redirect
   ========================================================================== */

window.AppUtils = window.AppUtils || {};

window.AppUtils.RoleModal = (function () {
    'use strict';

    /* -------------------------------------------------------------------------
       PRIVATE VARIABLES
       ------------------------------------------------------------------------- */

    var _modal = null;
    var _config = {
        selectRoleUrl: '/Account/SelectRole',
        sponsorRedirectUrl: '',
        advocateRedirectUrl: '',
        advocateNeedsCreation: false,
        eventId: 0,
        slug: '',
        advocateAlias: 'Advocate',
        sponsorAlias: 'Sponsor',
        contactAlias: 'Contact',
        beneficiaryAlias: 'Beneficiary'
    };
    var _isShowing = false;
    var _isProcessing = false;

    /* -------------------------------------------------------------------------
       PRIVATE FUNCTIONS - DOM
       ------------------------------------------------------------------------- */

    function _getModal() {
        if (!_modal) {
            _modal = document.getElementById('roleSelectionModal');
        }
        return _modal;
    }

    // Default Spanish translations for common English aliases
    var _aliasTranslations = {
        es: {
            'Advocate':    'Promotor',
            'Parent':      'Padre/Madre',
            'Player':      'Jugador',
            'Student':     'Estudiante',
            'Athlete':     'Atleta',
            'Member':      'Miembro',
            'Volunteer':   'Voluntario',
            'Sponsor':     'Patrocinador',
            'Contact':     'Contacto',
            'Supporter':   'Seguidor',
            'Donor':       'Donante',
            'Fan':         'Fanático',
            'Friend':      'Amigo',
            'Family':      'Familia',
            'Beneficiary': 'Beneficiario',
            'Child':       'Niño/a',
            'Team':        'Equipo'
        }
    };

    /**
     * Translate an alias if the current locale has a mapping.
     * Returns the original alias if no translation exists.
     */
    function _translateAlias(alias) {
        var lang = (window.i18n && window.i18n.lang) || 'en';
        if (lang === 'en' || !alias) return alias;

        var langMap = _aliasTranslations[lang];
        if (!langMap) return alias;

        // Try exact match (case-insensitive lookup)
        var key = Object.keys(langMap).find(function (k) {
            return k.toLowerCase() === alias.toLowerCase();
        });

        return key ? langMap[key] : alias;
    }

    function _updateAliases() {
        var modal = _getModal();
        if (!modal) return;

        // Update alias text throughout the modal
        var advocateAliasEls = modal.querySelectorAll('.js-advocate-alias');
        var advocateAliasPluralEls = modal.querySelectorAll('.js-advocate-alias-plural');
        var sponsorAliasEls = modal.querySelectorAll('.js-sponsor-alias');
        var contactAliasPluralEls = modal.querySelectorAll('.js-contact-alias-plural');
        var beneficiaryAliasPluralEls = modal.querySelectorAll('.js-beneficiary-alias-plural');

        var advocateAlias = _translateAlias(_config.advocateAlias || 'Advocate');
        var sponsorAlias = _translateAlias(_config.sponsorAlias || 'Sponsor');
        var contactAlias = _translateAlias(_config.contactAlias || 'Contact');
        var beneficiaryAlias = _translateAlias(_config.beneficiaryAlias || 'Beneficiary');

        advocateAliasEls.forEach(function (el) {
            el.textContent = advocateAlias;
        });

        advocateAliasPluralEls.forEach(function (el) {
            el.textContent = _pluralize(advocateAlias);
        });

        sponsorAliasEls.forEach(function (el) {
            el.textContent = sponsorAlias;
        });

        contactAliasPluralEls.forEach(function (el) {
            el.textContent = _pluralize(contactAlias);
        });

        beneficiaryAliasPluralEls.forEach(function (el) {
            el.textContent = _pluralize(beneficiaryAlias);
        });
    }

    function _pluralize(word) {
        if (!word) return word;

        // Skip pluralization for compound forms like "Padre/Madre" or "Niño/a"
        if (word.includes('/')) return word;

        var lang = (window.i18n && window.i18n.lang) || 'en';
        var lower = word.toLowerCase();

        if (lang === 'es') {
            // Spanish pluralization rules
            if ('aeiouáéíóú'.includes(lower[lower.length - 1])) {
                return word + 's';          // vocal → +s (Jugador-a → Jugador-as, Atleta → Atletas)
            }
            return word + 'es';             // consonant → +es (Promotor → Promotores)
        }

        // English pluralization rules
        if (lower.endsWith('y') && !'aeiou'.includes(lower[lower.length - 2])) {
            return word.slice(0, -1) + 'ies';
        }
        if (lower.endsWith('s') || lower.endsWith('x') || lower.endsWith('ch') || lower.endsWith('sh')) {
            return word + 'es';
        }
        return word + 's';
    }

    function _setRedirectUrls() {
        var modal = _getModal();
        if (!modal) return;

        var sponsorOption = modal.querySelector('[data-role="sponsor"]');
        var advocateOption = modal.querySelector('[data-role="advocate"]');

        if (sponsorOption) {
            sponsorOption.setAttribute('data-redirect-url', _config.sponsorRedirectUrl);
        }
        if (advocateOption) {
            advocateOption.setAttribute('data-redirect-url', _config.advocateRedirectUrl);
        }
    }

    function _setButtonsDisabled(disabled) {
        var modal = _getModal();
        if (!modal) return;

        var options = modal.querySelectorAll('.role-option');
        options.forEach(function (opt) {
            opt.disabled = disabled;
            opt.style.opacity = disabled ? '0.7' : '1';
            opt.style.pointerEvents = disabled ? 'none' : 'auto';
        });
    }

    function _setButtonLoading(button, isLoading) {
        if (!button) return;

        if (isLoading) {
            button.classList.add('is-loading');
            // Store original content
            button.dataset.originalContent = button.innerHTML;
            
            // Find the title element and update it
            var titleEl = button.querySelector('.role-option__title');
            if (titleEl) {
                titleEl.innerHTML = '<span class="spinner"></span> Loading...';
            }
        } else {
            button.classList.remove('is-loading');
            // Restore original content if stored
            if (button.dataset.originalContent) {
                button.innerHTML = button.dataset.originalContent;
                delete button.dataset.originalContent;
            }
        }
    }

    /* -------------------------------------------------------------------------
       PRIVATE FUNCTIONS - EVENTS
       ------------------------------------------------------------------------- */

    function _bindEvents() {
        var modal = _getModal();
        if (!modal) return;

        // Role option clicks
        var options = modal.querySelectorAll('.role-option');
        options.forEach(function (option) {
            // Remove any existing listeners first
            option.removeEventListener('click', _handleOptionClick);
            option.addEventListener('click', _handleOptionClick);
        });

        // Backdrop click - don't close, force user to make a choice
        var backdrop = modal.querySelector('.role-modal__backdrop');
        if (backdrop) {
            backdrop.addEventListener('click', function (e) {
                e.stopPropagation();
                // Could add a subtle shake animation here to indicate they must choose
            });
        }

        // Escape key - don't close, force user to make a choice
        document.addEventListener('keydown', _handleKeyDown);
    }

    function _unbindEvents() {
        document.removeEventListener('keydown', _handleKeyDown);
    }

    function _handleKeyDown(e) {
        if (e.key === 'Escape' && _isShowing) {
            // Don't close - force user to make a choice
            e.preventDefault();
        }
    }

    function _handleOptionClick(e) {
        if (_isProcessing) {
            return;
        }

        var button = e.currentTarget;
        var role = button.getAttribute('data-role');
        var directRedirectUrl = button.getAttribute('data-redirect-url');


        _isProcessing = true;

        // Disable all buttons to prevent double-click
        _setButtonsDisabled(true);
        _setButtonLoading(button, true);

        // Determine if we need to call SelectRole endpoint
        var needsServerCall = false;

        if (role === 'advocate') {
            // Always call server for advocate - either to create record or to add role
            needsServerCall = true;
        } else if (role === 'sponsor') {
            // Call server to add sponsor role
            needsServerCall = true;
        }

        if (needsServerCall) {
            _callSelectRole(role, button, directRedirectUrl);
        } else {
            // Fallback: direct redirect (shouldn't happen with new flow)
            _redirect(directRedirectUrl);
        }
    }

    /* -------------------------------------------------------------------------
       PRIVATE FUNCTIONS - API
       ------------------------------------------------------------------------- */

    function _callSelectRole(role, button, fallbackUrl) {
        // The modal usually opens right after an AJAX login on a page whose antiforgery token was
        // rendered while the visitor was still anonymous. Posting that stale token fails validation
        // ("meant for a different claims-based user") with a 400. Fetch a token bound to the now
        // authenticated user first; fall back to the DOM token if that request fails so this is never
        // worse than before.
        _fetchFreshToken(function (freshToken) {
            _postSelectRole(role, button, fallbackUrl, freshToken || _getAntiForgeryToken());
        });
    }

    function _fetchFreshToken(done) {
        fetch('/Account/AntiforgeryToken', {
            method: 'GET',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
        .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
        })
        .then(function (result) { done(result && result.token ? result.token : null); })
        .catch(function (error) {
            console.warn('[RoleModal] Could not refresh antiforgery token, using page token:', error);
            done(null);
        });
    }

    function _postSelectRole(role, button, fallbackUrl, token) {
        var url = _config.selectRoleUrl || '/Account/SelectRole';

        var data = {
            role: role,
            eventId: _config.eventId,
            slug: _config.slug
        };

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': token || '',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(data)
        })
        .then(function (response) {
            if (!response.ok) {
                throw new Error('HTTP ' + response.status);
            }
            return response.json();
        })
        .then(function (result) {

            if (result.success && result.redirectUrl) {
                _redirect(result.redirectUrl);
            } else if (result.success) {
                // Success but no redirect URL - use fallback
                console.warn('[RoleModal] No redirectUrl in response, using fallback');
                _redirect(fallbackUrl || '/');
            } else {
                // Error from server
                console.error('[RoleModal] SelectRole failed:', result.error);
                _showError(result.error || 'An error occurred. Please try again.');
                _resetButtons(button);
            }
        })
        .catch(function (error) {
            console.error('[RoleModal] SelectRole error:', error);
            _showError('An error occurred. Please try again.');
            _resetButtons(button);
        });
    }

    function _getAntiForgeryToken() {
        // Try multiple sources for the token
        if (window.AppUtils && window.AppUtils.getToken) {
            return window.AppUtils.getToken();
        }

        // Look for token in any form on the page
        var tokenInput = document.querySelector('input[name="__RequestVerificationToken"]');
        if (tokenInput) {
            return tokenInput.value;
        }

        // Look in meta tag (some setups put it there)
        var metaToken = document.querySelector('meta[name="csrf-token"]');
        if (metaToken) {
            return metaToken.getAttribute('content');
        }

        return null;
    }

    function _showError(message) {
        var modal = _getModal();
        if (!modal) return;

        // Find or create error container
        var errorContainer = modal.querySelector('.role-modal__error');
        if (!errorContainer) {
            errorContainer = document.createElement('div');
            errorContainer.className = 'role-modal__error';
            errorContainer.style.cssText = 'color: var(--r4o-danger-strong); background: var(--r4o-danger-soft); padding: 0.75rem 1rem; border-radius: 0.5rem; margin-top: 1rem; text-align: center; font-size: 0.875rem;';
            
            var body = modal.querySelector('.role-modal__body');
            if (body) {
                body.appendChild(errorContainer);
            }
        }

        errorContainer.textContent = message;
        errorContainer.style.display = 'block';

        // Auto-hide after 5 seconds
        setTimeout(function () {
            if (errorContainer) {
                errorContainer.style.display = 'none';
            }
        }, 5000);
    }

    function _resetButtons(clickedButton) {
        _isProcessing = false;
        _setButtonsDisabled(false);
        _setButtonLoading(clickedButton, false);
    }

    function _redirect(url) {
        if (url) {
            window.location.href = url;
        } else {
            console.warn('[RoleModal] No redirect URL provided');
            _isProcessing = false;
            _setButtonsDisabled(false);
            hide();
        }
    }

    /* -------------------------------------------------------------------------
       PUBLIC FUNCTIONS
       ------------------------------------------------------------------------- */

    /**
     * Initialize the role modal with configuration
     * @param {Object} config - Configuration object
     * @param {string} config.selectRoleUrl - URL to POST role selection (default: /Account/SelectRole)
     * @param {string} config.sponsorRedirectUrl - URL for Sponsor dashboard (fallback)
     * @param {string} config.advocateRedirectUrl - URL for Advocate dashboard/onboarding (fallback)
     * @param {boolean} config.advocateNeedsCreation - Whether advocate record needs to be created
     * @param {number} config.eventId - Current event ID
     * @param {string} config.slug - Event slug
     * @param {string} config.advocateAlias - Custom term for Advocate
     * @param {string} config.sponsorAlias - Custom term for Sponsor
     * @param {string} config.contactAlias - Custom term for Contact
     * @param {string} config.beneficiaryAlias - Custom term for Beneficiary
     */
    function init(config) {
        if (config) {
            _config = Object.assign({}, _config, config);
        }
        
        _isProcessing = false;
        _updateAliases();
        _setRedirectUrls();
        _bindEvents();
        
    }

    /**
     * Show the role selection modal
     */
    function show() {
        var modal = _getModal();
        if (!modal) {
            console.error('[RoleModal] Modal element not found');
            return;
        }

        // Reset state
        _isProcessing = false;
        _setButtonsDisabled(false);

        // Hide any previous error
        var errorContainer = modal.querySelector('.role-modal__error');
        if (errorContainer) {
            errorContainer.style.display = 'none';
        }

        modal.style.display = 'flex';
        _isShowing = true;

        // Focus first option for accessibility
        setTimeout(function () {
            var firstOption = modal.querySelector('.role-option');
            if (firstOption) {
                firstOption.focus();
            }
        }, 100);

    }

    /**
     * Hide the role selection modal
     */
    function hide() {
        var modal = _getModal();
        if (modal) {
            modal.style.display = 'none';
        }
        _isShowing = false;
        _isProcessing = false;
    }

    /**
     * Check if modal is currently showing
     * @returns {boolean}
     */
    function isShowing() {
        return _isShowing;
    }

    /**
     * Check if modal is currently processing a selection
     * @returns {boolean}
     */
    function isProcessing() {
        return _isProcessing;
    }

    /**
     * Update configuration
     * @param {Object} config - New configuration values to merge
     */
    function configure(config) {
        if (config) {
            _config = Object.assign({}, _config, config);
            _updateAliases();
            _setRedirectUrls();
        }
    }

    /**
     * Get current configuration
     * @returns {Object}
     */
    function getConfig() {
        return Object.assign({}, _config);
    }

    /**
     * Destroy the modal (cleanup)
     */
    function destroy() {
        _unbindEvents();
        hide();
        _modal = null;
    }

    /* -------------------------------------------------------------------------
       PUBLIC API
       ------------------------------------------------------------------------- */

    return {
        init: init,
        show: show,
        hide: hide,
        isShowing: isShowing,
        isProcessing: isProcessing,
        configure: configure,
        getConfig: getConfig,
        destroy: destroy
    };

})();

/* =============================================================================
   GLOBAL ALIAS
   ========================================================================== */

window.RoleModal = window.AppUtils.RoleModal;
